import { SourceGlyph } from '@flexbyt/widget-preview';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AgenciesTeaser } from '../components/home/AgenciesTeaser.js';
import { CustomerMarquee } from '../components/home/CustomerMarquee.js';
import { DisplayResult } from '../components/home/DisplayResult.js';
import { FeaturedQuote } from '../components/home/FeaturedQuote.js';
import { HeroShowcase } from '../components/home/HeroShowcase.js';
import { HowItWorks } from '../components/home/HowItWorks.js';
import { ImportSources } from '../components/home/ImportSources.js';
import { ManageLibrary } from '../components/home/ManageLibrary.js';
import { SourcesStrip } from '../components/home/SourcesStrip.js';
import { PlayJet } from '../components/mascot/scenes.js';
import {
  ButtonAnchor,
  ButtonHashLink,
  ButtonLink,
} from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Frame, NightStage } from '../components/ui/surface.js';
import { Heading, Mark, Text } from '../components/ui/type.js';
import {
  featuredTestimonial,
  fetchTestimonials,
} from '../content/testimonials.js';
import {
  WIDGET_BUILDER_PATH,
  WIDGET_COUNT_WORD,
} from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';

/**
 * The homepage.
 *
 * ===========================================================================
 * One story, told once
 * ===========================================================================
 * Reviews are scattered → ReviewsJet gathers them → you choose which ones →
 * they go on your website. Every section advances that sentence by exactly one
 * step, and no section repeats a step another one has already made.
 *
 * ===========================================================================
 * The rule this page is built around
 * ===========================================================================
 * A previous version showed reviews in four places — filtered by platform in
 * the hero, as ReviewsJet's own testimonials, inside the management UI, and
 * across several switchable widget previews. Each was defensible alone. Read
 * top to bottom they made the page feel like one component rendered over and
 * over, and worse, they asked a first-time visitor to understand the difference
 * between masonry and grid before they understood what the product was.
 *
 * So every place reviews appear is visibly a different kind of thing, and the
 * chrome around it is what says which:
 *
 *   Import    a ReviewsJet screen — a paste field and a list of connections
 *   Manage    a ReviewsJet screen — rows with checkboxes, and the grid widget
 *             those rows feed, so publishing is watchable rather than claimed
 *   Display   a browser, on somebody else's domain — one finished website,
 *             one layout, no controls
 *
 * All three are drawn by `AppPanel` and `SitePanel`, which exist for exactly
 * this reason — see `components/home/Panel.tsx`. Everywhere else the visuals
 * are sources, connections, steps and workflow.
 *
 * ReviewsJet's *own* customer reviews were a fourth set here — a whole grid of
 * them — and that grid is now on `/pricing`, which is where somebody asks the
 * question it answers. A visitor on this page is still working out what the
 * product is; a section of testimony answers "is it worth paying for", which is
 * a question three screens further on.
 *
 * What stayed is a *single* review, set in the quote widget and folded into
 * the sources section rather than given a band of its own. That is a different
 * argument in a different shape and it does not reopen the problem above: one
 * tilted card is not a wall of them, and it is the only thing on the page that
 * is neither a demonstration nor a diagram.
 * See `components/home/FeaturedQuote.tsx`.
 *
 * Layout choice, customisation and the full set of widget types are not on this
 * page at all. They are the free builder's job, and the Display call to action is the
 * door to it. Level 1 explains the product; level 2 lets you play with it.
 *
 * ===========================================================================
 * Section shapes
 * ===========================================================================
 * Deliberately varied so the page does not read as one template repeated: a
 * three-column diagram, a horizontal strip, a three-step row, an asymmetric
 * split, a full-width application panel, a lifted website, a workflow list, and
 * a quiet dark close.
 */
export const Route = createFileRoute('/')({
  head: () =>
    seo({
      title: 'ReviewsJet — bring your reviews together',
      description:
        'Import reviews from Google, Trustpilot, Yelp, Tripadvisor, Airbnb, the App Store and Google Play, choose the ones that matter, and display them beautifully on your website.',
      path: '/',
    }),
  /*
    Loaded on the server, so the one customer review this page prints is in the
    HTML a crawler receives. Social proof that only appears after hydration does
    not exist as far as search is concerned.

    The *section* of ReviewsJet's own reviews moved to `/pricing` — see
    `components/Testimonials.tsx`. What is left here is a single quotation,
    which is a different argument made in a different shape; the fetch is the
    same one, and it never throws.
  */
  loader: () => fetchTestimonials(),
  component: Home,
});

/**
 * Whose review the homepage prints.
 *
 * A name rather than the words, so the quotation itself still comes from the
 * API — see `featuredTestimonial` for why a pasted testimonial is the one thing
 * that file exists to prevent, and for what happens when this name no longer
 * matches anybody.
 */
const FEATURED_AUTHOR = 'EPIKDRIVES';

/** Where the closing band's source marks sit, and how far each is knocked off true. */
const SCATTERED = [
  { source: 'google', top: '4%', left: '6%', tilt: -8, delay: '0s' },
  { source: 'trustpilot', top: '0%', left: '58%', tilt: 7, delay: '-1.2s' },
  { source: 'yelp', top: '38%', left: '16%', tilt: 11, delay: '-2.4s' },
  { source: 'tripadvisor', top: '66%', left: '0%', tilt: -12, delay: '-3.6s' },
  { source: 'airbnb', top: '62%', left: '66%', tilt: -5, delay: '-4.8s' },
  { source: 'appstore', top: '30%', left: '84%', tilt: 9, delay: '-6s' },
  { source: 'googleplay', top: '78%', left: '34%', tilt: -9, delay: '-7.2s' },
] as const;

function Home() {
  const featured = featuredTestimonial(Route.useLoaderData(), FEATURED_AUTHOR);

  return (
    <>
      {/* ----------------------------------------------------------- Hero */}
      {/*
        The flexbyt.com hero: centred, a badge, a very large headline with one
        word in brand blue, two pill actions — and the product on a dark stage
        underneath, wide enough to read, instead of squeezed into half a column.
      */}
      <Section
        size="tight"
        width="wide"
        className="relative isolate overflow-x-clip pt-14 md:pt-20"
      >
        <div
          aria-hidden
          className="grid-fade pointer-events-none absolute inset-x-0 -top-[4.5rem] bottom-0 -z-10"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 -top-72 -z-10 h-[44rem] w-[70rem] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(31,91,240,0.14),transparent)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute top-40 -right-40 -z-10 size-[30rem] rounded-full bg-[radial-gradient(closest-side,rgba(109,60,245,0.08),transparent)]"
        />

        <div className="mx-auto max-w-5xl text-center">
          <div className="rj-rise flex justify-center">
            <Link
              to="/for/$platform"
              params={{ platform: 'wix' }}
              className="group inline-flex items-center gap-2.5 rounded-full bg-white py-1 pr-3.5 pl-1 text-caption font-semibold text-ink-600 shadow-[0_1px_2px_rgba(10,10,11,0.04)] ring-1 ring-black/[0.08] transition-colors hover:ring-black/15"
            >
              <span className="rounded-full bg-brand px-2.5 py-0.5 text-[12px] font-bold text-white">
                New
              </span>
              Native apps for Wix &amp; Webflow
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-3.5 transition-transform group-hover:translate-x-0.5"
                aria-hidden
              >
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </Link>
          </div>

          {/*
            The headline states the visitor's situation, not the product's
            category: "your reviews are scattered" tells somebody something true
            about themselves, which is what makes a stranger read the next line.
          */}
          <Heading
            level={1}
            size="hero"
            className="rj-rise rj-delay-1 mx-auto mt-8 max-w-5xl text-[2.9rem] leading-[0.98] font-extrabold tracking-[-0.045em] sm:text-7xl lg:text-[5.75rem]"
          >
            Your reviews are scattered. Bring them{' '}
            <span className="whitespace-nowrap">
              <Mark className="marked-plain text-brand">together</Mark>.
            </span>
          </Heading>

          <Text
            size="lead"
            className="rj-rise rj-delay-2 mx-auto mt-7 max-w-2xl sm:text-xl sm:leading-9"
          >
            Import from Google, Trustpilot, Yelp, Tripadvisor, Airbnb and the
            app stores. Choose the ones worth showing. Put them on your website.
          </Text>

          <div className="rj-rise rj-delay-3 mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            <ButtonAnchor
              href={startUrl('homepage-hero')}
              size="lg"
              className="h-14 px-8"
              onClick={() =>
                track('hero_cta_clicked', { surface: 'homepage-hero' })
              }
            >
              Start free
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-4"
                aria-hidden
              >
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </ButtonAnchor>
            {/*
              Down the page, not off it: the explanation a first-time visitor
              wants is the next four sections.
            */}
            <ButtonHashLink
              hash="how-it-works"
              variant="secondary"
              size="lg"
              className="h-14 px-8"
            >
              See how it works
            </ButtonHashLink>
          </div>

          <ul className="rj-rise rj-delay-4 mt-7 flex flex-col items-center justify-center gap-x-6 gap-y-2 text-caption text-ink-600 sm:flex-row sm:flex-wrap">
            {[
              'Free plan, no card',
              'Works on Wix, Webflow, WordPress and Squarespace',
            ].map((fact) => (
              <li key={fact} className="flex items-center gap-2">
                <CheckMark />
                {fact}
              </li>
            ))}
          </ul>
        </div>

        <div className="rj-rise rj-delay-4 relative mt-16 md:mt-20">
          <HeroShowcase />
          {/* Jet, hovering over the product's top edge and waving people in. */}
          <PlayJet
            mood="wave"
            className="absolute -top-14 right-[7%] z-10 w-20 sm:-top-20 sm:w-28 lg:w-32"
          />
        </div>

        {/*
          The trust strip closes the hero rather than opening a section of its
          own: "who else uses this" is a question a visitor has while reading the
          headline, not a topic that deserves its own band.
        */}
        <CustomerMarquee className="mt-16 md:mt-20" />
      </Section>

      {/* -------------------------------------------------------- Sources */}
      {/*
        One customer's review rides in this section rather than having a band of
        its own, in the space the heading block was leaving empty on a wide
        screen. The pairing is the point: the heading makes the page's first
        factual claim — these are the platforms we read — and the review beside
        it is the first evidence that the claim is worth anything.

        Absent on a deployment with no workspace configured, in which case the
        section is exactly what it was before. See `FeaturedQuote`.
      */}
      <Section tone="raised" size="tight" aria-label="Supported review sources">
        <SourcesStrip
          aside={featured ? <FeaturedQuote review={featured} /> : undefined}
        />
      </Section>

      {/* --------------------------------------------------- How it works */}
      <Section id="how-it-works" aria-labelledby="how-heading">
        <div id="how-heading" className="sr-only">
          How ReviewsJet works
        </div>
        <HowItWorks />
      </Section>

      {/* --------------------------------------------------------- Import */}
      <Section tone="raised" aria-labelledby="import-heading">
        <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] lg:items-center lg:gap-16">
          <div>
            <StepMark n="01" label="Import" />
            <Heading level={2} id="import-heading" className="mt-5">
              Your customers already left the reviews.
            </Heading>
            <Text className="mt-4 max-w-prose">
              You do not have to ask anyone for anything. Connect the listings
              your reviews are already on and ReviewsJet brings them across,
              then keeps checking for new ones.
            </Text>
            <Text className="mt-4 max-w-prose">
              One platform, several listings — three shops, two hotels, a
              property each — all arrive in the same library, still labelled
              with where they came from.
            </Text>
          </div>

          <div className="relative isolate">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-10 -z-10 rounded-full bg-[radial-gradient(closest-side,rgba(31,91,240,0.14),transparent)]"
            />
            <Frame>
              <ImportSources />
            </Frame>
          </div>
        </div>
      </Section>

      {/* --------------------------------------------------------- Manage */}
      <Section aria-labelledby="manage-heading">
        <Measure>
          <StepMark n="02" label="Manage" />
          <Heading level={2} id="manage-heading" className="mt-5">
            Everything in one review library.
          </Heading>
          <Text className="mt-4 max-w-prose">
            You are in control of what gets displayed. Import everything, then
            choose which reviews appear on your website — one at a time, or
            fifty at once. Nothing you leave unpublished ever reaches your site.
          </Text>
        </Measure>

        <div className="mt-10">
          {/*
            `preview` puts the real grid widget under the library, fed by the
            published rows alone. It is the one change on this page that turns
            a claim into a demonstration: "nothing goes live until you say so"
            is a sentence, and a review leaving the widget the moment you
            unpublish it is the thing the sentence is about.
          */}
          <Frame className="p-2 sm:p-3">
            <ManageLibrary preview />
          </Frame>
          <p className="mt-4 text-caption text-ink-500">
            Both halves are live — publish or unpublish a review, or select
            several and do it in bulk, and watch the widget underneath change.
            Nothing here touches a real account, and reloading brings everything
            back.
          </p>
        </div>
      </Section>

      {/* -------------------------------------------------------- Display */}
      {/*
        The one tinted band on the page, and this is the section that earns it:
        it is the payoff of Import → Manage → Display, and the only place the
        page asks for a second action. Everything above it alternated white and
        `ink-25`, so arriving here should feel like arriving somewhere.
      */}
      <Section tone="tinted" aria-labelledby="display-heading">
        <Measure>
          <StepMark n="03" label="Display" />
          <Heading level={2} id="display-heading" className="mt-5">
            Your best reviews, where they matter most.
          </Heading>
          <Text className="mt-4 max-w-prose">
            The reviews you published become a section of your own website —
            styled to fit, updating itself as new ones come in. Paste one line
            of code, or install the app if you are on Wix or Webflow. This is a
            masonry wall on a hotel&rsquo;s reviews page; there are{' '}
            {WIDGET_COUNT_WORD} layouts to choose from.
          </Text>
        </Measure>

        <NightStage className="mt-12" innerClassName="px-3 pt-8 sm:px-10 sm:pt-12">
          <div className="relative -mb-px">
            <DisplayResult />
          </div>
        </NightStage>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
          {/*
            Widget discovery starts here and nowhere earlier. Somebody who has
            read this far knows what the product does and has just seen the
            result — which is the first moment "which layout?" is a question
            they have rather than one being put to them.
          */}
          <ButtonLink
            to={WIDGET_BUILDER_PATH}
            size="lg"
            onClick={() =>
              track('widget_selected', { surface: 'homepage-display' })
            }
          >
            Build a free review widget
          </ButtonLink>
          <ButtonLink to="/widgets" variant="secondary" size="lg">
            See every layout
          </ButtonLink>
        </div>
      </Section>

      {/* ------------------------------------------------------- Agencies */}
      <Section tone="raised" aria-labelledby="agencies-heading">
        <AgenciesTeaser />
      </Section>

      {/* ---------------------------------------------------------- Close */}
      {/*
        flexbyt.com's closing band: a light surface card, a very large two-line
        headline with the second line in brand blue, and the action set small
        and bottom-aligned beside it. Light rather than dark so it does not
        merge with the footer, which is the page's one night band.

        Copy and actions together on the left; the right half is the five
        source marks scattered, tilted and drifting — the word "scattered",
        drawn. Decoration only.
      */}
      <Section size="tight" width="wide" aria-labelledby="close-heading">
        <div className="relative isolate overflow-hidden rounded-[2.5rem] bg-surface px-7 py-12 sm:px-14 sm:py-14">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -right-24 -z-10 size-96 rounded-full bg-brand/15 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-32 left-1/3 -z-10 size-80 rounded-full bg-grape/10 blur-3xl"
          />

          <div className="relative grid items-center gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <h2
                id="close-heading"
                className="text-[2.75rem] leading-[0.98] font-extrabold tracking-[-0.045em] text-balance text-ink-950 sm:text-6xl"
              >
                Your best reviews shouldn&rsquo;t stay{' '}
                <span className="text-brand">scattered.</span>
              </h2>

              <p className="mt-5 max-w-xl text-body-lg text-ink-600">
                Bring them in from Google, Trustpilot, Yelp, Tripadvisor,
                Airbnb, the App Store and Google Play, choose the ones worth
                showing, and put them on your website.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <ButtonAnchor
                  href={startUrl('homepage-close')}
                  size="lg"
                  onClick={() =>
                    track('hero_cta_clicked', { surface: 'homepage-close' })
                  }
                >
                  Start free
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="size-4"
                    aria-hidden
                  >
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </ButtonAnchor>
                <ButtonLink to="/pricing" variant="secondary" size="lg">
                  See pricing
                </ButtonLink>
              </div>
              <p className="mt-4 text-caption text-ink-500">
                Free plan, no card. Cancel whenever you like.
              </p>
            </div>

            {/* The word "scattered", drawn: the five marks, knocked off true. */}
            <ul
              aria-hidden
              className="relative mx-auto h-44 w-full max-w-sm sm:h-56 lg:col-span-5 lg:h-60 lg:max-w-none"
            >
              {/* Jet in the middle of them, holding the one that matters. */}
              <li className="absolute top-[10%] left-[38%] z-10 w-28 sm:w-36">
                <PlayJet mood="hold" />
              </li>
              {SCATTERED.map((mark) => (
                <li
                  key={mark.source}
                  className="rj-float absolute grid size-16 place-items-center rounded-[1.25rem] bg-white shadow-[0_24px_48px_-20px_rgba(20,40,120,0.4)] ring-1 ring-black/[0.05] sm:size-20 sm:rounded-[1.5rem]"
                  style={{
                    top: mark.top,
                    left: mark.left,
                    rotate: `${mark.tilt}deg`,
                    animationDelay: mark.delay,
                  }}
                >
                  <SourceGlyph source={mark.source} className="size-8 sm:size-10" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>
    </>
  );
}

/**
 * The step number and its verb.
 *
 * Carries Import → Manage → Display through the three product sections without
 * spending a section on a summary of the sections that follow it. The number is
 * set in the display face at a size that reads as a chapter mark rather than as
 * data.
 *
 * ---------------------------------------------------------------------------
 * The numeral is the page's spine, so it is the page's colour
 * ---------------------------------------------------------------------------
 * It was `ink-200` — light grey on white, which is the value you pick when the
 * mark is decoration you are slightly embarrassed by. But these three numerals
 * are the only thing tying four screens of scrolling into one sequence, and a
 * reader who lands halfway down the page reads the numeral first to work out
 * where they are.
 *
 * So it takes `accent-400`, which the token block designates for exactly this —
 * indicators, the marks that carry meaning on their own and therefore have to
 * clear 3:1 unaided. The label beside it stays neutral; two coloured things
 * next to each other would make it a badge rather than a chapter mark.
 */
/**
 * The tick beside a fact in the hero.
 *
 * `positive-700` and not the accent: these are two statements of fact, and the
 * accent already belongs to the primary button eighteen pixels above them. A
 * second blue mark that close would read as another action.
 */
function CheckMark() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-3.5 shrink-0 text-positive-700"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m3.5 8.5 3 3 6-7" />
    </svg>
  );
}

function StepMark({ n, label }: { n: string; label: string }) {
  return (
    <p className="inline-flex items-center gap-2.5 rounded-full bg-white py-1 pr-4 pl-1 shadow-[0_1px_2px_rgba(10,10,11,0.04)] ring-1 ring-black/[0.08]">
      <span className="nums rounded-full bg-brand px-2.5 py-0.5 text-caption font-bold text-white">
        {n}
      </span>
      <span className="text-caption font-semibold tracking-wide text-ink-700">
        {label}
      </span>
    </p>
  );
}
